import {
  IQChat,
  IQChatFrom,
  LangGraphConversation,
  toChatHistory,
} from '@maximizer/ai/data-access';
import { IQBoostChatOverride } from '../overrides/iqboost-chat-override';

// An instruction to the orchestrator, not the reader's words: never translated, never shown. The
// wording is explained in docs/ai/rich-widgets-rendering.md.
export const RICH_UI_INSTRUCTION =
  'Where your results take one of these shapes, show them with the matching rich UI widget as ' +
  'well as describing them: headline or coverage figures as nxstats; a ranking, a trend or a ' +
  'share of a whole as nxchart; dated events as nxtimeline; a few households, accounts or ' +
  'policies compared as nxcard; one client or household introduced as nxprofile; the facts about ' +
  'one record as nxfacts; what is on file and what is missing, or done and overdue, as nxlist; ' +
  'rows of records with figures to align as nxtable; a short set of flags or statuses as ' +
  'nxchiplist. Say in a sentence what each widget shows, and never repeat its values in a list, ' +
  'a table or chips. Where none of these shapes fits, answer in prose alone.';

/** How the instruction rides on the question; stripped again when a stored thread is reopened. */
export const RICH_UI_SUFFIX = `\n\n${RICH_UI_INSTRUCTION}`;

// Threads stored while an earlier wording was in use still end their questions with it.
const LEGACY_RICH_UI_SUFFIXES: readonly string[] = [
  '\n\nShow the results by using the rich UI widgets.',
];

const RICH_UI_SUFFIXES: readonly string[] = [
  RICH_UI_SUFFIX,
  ...LEGACY_RICH_UI_SUFFIXES,
];

/**
 * Shows a stored question as the reader typed it, without the rich-UI instruction. Only questions
 * stored before the question as shown was kept with them still carry it.
 */
const withoutRichUiInstruction = (chat: IQChat): IQChat => {
  if (chat.from !== IQChatFrom.User) {
    return chat;
  }

  const suffix = RICH_UI_SUFFIXES.find((candidate) =>
    chat.content.endsWith(candidate),
  );

  return suffix
    ? { ...chat, content: chat.content.slice(0, -suffix.length) }
    : chat;
};

export function presentAnswer(
  overrides: readonly IQBoostChatOverride[],
  answer: string,
): string {
  return overrides.reduce(
    (presented, override) => override.presentAnswer?.(presented) ?? presented,
    answer,
  );
}

export function readChatHistory(
  { messages, handoffs, confirmations }: LangGraphConversation,
  overrides: readonly IQBoostChatOverride[],
): IQChat[] {
  const history = toChatHistory(messages, handoffs, confirmations).map(withoutRichUiInstruction);
  if (overrides.length === 0) {
    return history;
  }
  return (
    history
      .map((chat) =>
        chat.from === IQChatFrom.Ai
          ? { ...chat, content: presentAnswer(overrides, chat.content) }
          : chat,
      )
      // An answer left with nothing to show goes, unless an override shows something under it.
      .filter(
        (chat) =>
          chat.from === IQChatFrom.User ||
          chat.content.length > 0 ||
          chat.addon ||
          chat.handoffs ||
          chat.confirmations,
      )
  );
}

// A thread still at work may have saved its answer before its run ends: that answer is the run's.
export function untilLastQuestion(history: IQChat[]): IQChat[] {
  const last = history.map((chat) => chat.from).lastIndexOf(IQChatFrom.User);
  return history.slice(0, last + 1);
}
